import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight, Menu, X } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { APPS, appPath } from '@/apps/data';

/**
 * Slim, translucent navigation — Apple idiom: 52px bar, small type, one quiet CTA.
 * "Plataforma" is a real link (click → /plataforma) and, on hover, opens a full-width
 * flyout with the apps — like the Apple.com product menus. Closes on leave / Escape /
 * route change. Transparent over the hero, frosted once the page scrolls or the flyout opens.
 */
const agencyLinks = [
  { label: 'Cómo funciona', path: '/como-funciona' },
  { label: 'Planes y precios', path: '/plataforma#planes' },
  { label: 'Para transitarias', path: '/plataforma#transitarias' },
  { label: 'Servicios en detalle', path: '/servicios' },
  { label: 'Solicita una demo', path: '/plataforma#solicitar' },
];

const ourWorkLinks = [
  { label: 'Blog', path: '/blog' },
  { label: 'Anti-Fraude', path: '/anti-fraude' },
  { label: 'Filantropía', path: '/filantropia' },
];

export function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const closeTimer = useRef<number | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 24);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close everything on navigation
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setFlyoutOpen(false);
  }, [location.pathname, location.hash]);

  // Escape closes the flyout
  useEffect(() => {
    if (!flyoutOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setFlyoutOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [flyoutOpen]);

  const openFlyout = useCallback(() => {
    if (closeTimer.current) { window.clearTimeout(closeTimer.current); closeTimer.current = null; }
    setFlyoutOpen(true);
  }, []);
  const scheduleClose = useCallback(() => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setFlyoutOpen(false), 120);
  }, []);

  const isActive = (path: string) => location.pathname === path;
  const isPlatformActive = location.pathname.startsWith('/plataforma');

  const navLinks = [
    { label: 'Inicio', path: '/' },
    { label: 'Cómo funciona', path: '/como-funciona' },
    { label: 'Plataforma', path: '/plataforma', flyout: true },
    { label: 'Socios', path: '/socios' },
    { label: 'Clientes', path: '/clientes' },
  ];

  const frosted = isScrolled || isMobileMenuOpen || flyoutOpen;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
          frosted ? 'bg-white/85 backdrop-blur-xl hairline' : 'bg-transparent'
        }`}
        onMouseLeave={scheduleClose}
      >
        <div className="mx-auto w-full max-w-[1120px] px-6 sm:px-8">
          <div className="flex items-center justify-between h-[52px]">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" onMouseEnter={scheduleClose}>
              <img src="/logo-96.png" alt="Mi Envío Cuba" width="28" height="28" className="w-7 h-7 object-contain" />
              <span className="font-semibold text-[15px] tracking-tight text-text-primary">Mi Envío Cuba</span>
            </Link>

            {/* Desktop */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) =>
                link.flyout ? (
                  <Link
                    key={link.path}
                    to={link.path}
                    onMouseEnter={openFlyout}
                    onFocus={openFlyout}
                    aria-haspopup="true"
                    aria-expanded={flyoutOpen}
                    className={`flex items-center gap-1 text-[13px] font-medium transition-colors ${
                      isPlatformActive || flyoutOpen ? 'text-text-primary' : 'text-text-secondary hover:text-text-primary'
                    }`}
                  >
                    {link.label}
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${flyoutOpen ? 'rotate-180' : ''}`} />
                  </Link>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    onMouseEnter={scheduleClose}
                    className={`text-[13px] font-medium transition-colors ${
                      isActive(link.path) ? 'text-text-primary' : 'text-text-secondary hover:text-text-primary'
                    }`}
                  >
                    {link.label}
                  </Link>
                )
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    onMouseEnter={scheduleClose}
                    className="flex items-center gap-1 text-[13px] font-medium text-text-secondary hover:text-text-primary transition-colors"
                  >
                    Nuestro trabajo
                    <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center" className="w-44 rounded-2xl">
                  {ourWorkLinks.map((link) => (
                    <DropdownMenuItem key={link.path} asChild>
                      <Link to={link.path} className="cursor-pointer">{link.label}</Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* CTA */}
            <div className="hidden lg:block" onMouseEnter={scheduleClose}>
              <Link
                to="/plataforma#solicitar"
                className="inline-flex items-center rounded-full bg-brand text-white text-[13px] font-medium px-4 h-8 hover:bg-brand-deep transition-colors"
              >
                Solicita una demo
              </Link>
            </div>

            {/* Mobile toggle */}
            <button
              className="lg:hidden p-2 -mr-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <X className="w-5 h-5 text-text-primary" /> : <Menu className="w-5 h-5 text-text-primary" />}
            </button>
          </div>
        </div>

        {/* Desktop flyout — full width, hover-driven, like apple.com */}
        <div
          onMouseEnter={openFlyout}
          onMouseLeave={scheduleClose}
          className={`hidden lg:block absolute inset-x-0 top-full overflow-hidden transition-all duration-300 ease-out ${
            flyoutOpen ? 'max-h-[560px] opacity-100' : 'max-h-0 opacity-0 pointer-events-none'
          }`}
          aria-hidden={!flyoutOpen}
        >
          <div className="bg-white hairline-top">
            <div className="mx-auto w-full max-w-[1120px] px-6 sm:px-8 py-10 grid grid-cols-[1.6fr_1fr] gap-16">
              {/* Apps */}
              <div>
                <p className="text-xs font-semibold text-text-tertiary mb-4">La plataforma</p>
                <div className="grid grid-cols-2 gap-x-10 gap-y-1">
                  {APPS.map((app) => (
                    <Link
                      key={app.slug}
                      to={appPath(app.slug)}
                      tabIndex={flyoutOpen ? 0 : -1}
                      className="group flex items-center gap-4 rounded-2xl px-3 py-3 -mx-3 hover:bg-bg-light transition-colors"
                    >
                      <img src={app.logo} alt="" width={44} height={44} className="w-11 h-11 rounded-xl shrink-0" />
                      <span className="min-w-0">
                        <span className="flex items-center gap-2 text-[17px] font-semibold tracking-tight text-text-primary">
                          {app.name}
                          {app.status === 'en-camino' && (
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">En camino</span>
                          )}
                        </span>
                        <span className="block text-[13px] text-text-secondary leading-snug">{app.tagline}</span>
                      </span>
                    </Link>
                  ))}
                  <Link
                    to="/plataforma"
                    tabIndex={flyoutOpen ? 0 : -1}
                    className="group flex items-center gap-4 rounded-2xl px-3 py-3 -mx-3 hover:bg-bg-light transition-colors"
                  >
                    <span className="w-11 h-11 rounded-xl bg-brand/10 flex items-center justify-center shrink-0">
                      <ChevronRight className="w-5 h-5 text-brand transition-transform group-hover:translate-x-0.5" />
                    </span>
                    <span>
                      <span className="block text-[17px] font-semibold tracking-tight text-brand">Ver toda la plataforma</span>
                      <span className="block text-[13px] text-text-secondary leading-snug">Cómo encajan las apps, planes y demo.</span>
                    </span>
                  </Link>
                </div>
              </div>

              {/* For agencies */}
              <div>
                <p className="text-xs font-semibold text-text-tertiary mb-4">Para tu agencia</p>
                <ul className="space-y-2.5">
                  {agencyLinks.map((l) => (
                    <li key={l.path}>
                      <Link
                        to={l.path}
                        tabIndex={flyoutOpen ? 0 : -1}
                        className="text-[15px] font-medium text-text-secondary hover:text-text-primary transition-colors"
                      >
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white/95 backdrop-blur-xl hairline-top">
            <div className="px-6 py-6 space-y-1">
              {navLinks.map((link) => (
                <div key={link.path}>
                  <Link
                    to={link.path}
                    className={`block py-3 text-xl font-semibold tracking-tight ${isActive(link.path) || (link.flyout && isPlatformActive) ? 'text-text-primary' : 'text-text-secondary'}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                  {link.flyout && (
                    <div className="pl-1 pb-2 space-y-1">
                      {APPS.map((app) => (
                        <Link
                          key={app.slug}
                          to={appPath(app.slug)}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="flex items-center gap-3 py-2 text-base text-text-secondary"
                        >
                          <img src={app.logo} alt="" width={28} height={28} className="w-7 h-7 rounded-lg" />
                          <span>{app.name}{app.status === 'en-camino' ? <span className="ml-2 text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">En camino</span> : null}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="pt-4 mt-2 hairline-top">
                <p className="pt-4 text-xs font-semibold text-text-tertiary uppercase tracking-wider mb-1">Nuestro trabajo</p>
                {ourWorkLinks.map((link) => (
                  <Link key={link.path} to={link.path} className="block py-2 text-base text-text-secondary" onClick={() => setIsMobileMenuOpen(false)}>
                    {link.label}
                  </Link>
                ))}
              </div>
              <Link
                to="/plataforma#solicitar"
                onClick={() => setIsMobileMenuOpen(false)}
                className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-brand text-white font-medium h-12"
              >
                Solicita una demo
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Page dim behind the open flyout (apple.com style). Hovering it closes the menu. */}
      <div
        aria-hidden="true"
        onMouseEnter={() => setFlyoutOpen(false)}
        className={`hidden lg:block fixed inset-x-0 top-[52px] bottom-0 z-40 bg-black/25 backdrop-blur-[2px] transition-opacity duration-300 ${
          flyoutOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      />
    </>
  );
}
